import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';
import getAllMatchingVariations from 'editor/src/store/variants/helpers/getAllMatchingVariations';
import changeEcommerceVariantPageCountOperation from 'editor/src/store/variants/operation/changeEcommerceVariantPageCountOperation';
import checkForMissingTemplatesOperation from 'editor/src/store/variants/operation/checkForMissingTemplatesOperation';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';
import { setPageCountAction } from 'editor/src/store/variants/slice';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import { removeSpreadAction } from '../../slice';
import getSpreadCountForIncrement from '../../util/getSpreadCountForIncrement';

const removeSpreadOperation =
  (spreadIndex: number): Thunk =>
  (dispatch, getState) => {
    batch(() => {
      const { designData } = getState().design;
      // Out-of-range or non-removable target: no-op, keeping the pre-loop
      // behavior where an invalid splice index removed nothing.
      if (!designData || !designData.spreads[spreadIndex]?.canRemove) {
        return;
      }

      // Mirror the add path: remove a full grid step worth of spreads so the
      // resulting page count stays on the valid grid.
      const spreadsToRemove = getSpreadCountForIncrement(designData, spreadIndex, getECommercePageCounts(getState()));

      dispatch(saveUndoRedoStateOperation('remove spread'));

      for (let i = 0; i < spreadsToRemove; i += 1) {
        const currentData = getState().design.designData;
        if (!currentData || currentData.spreads.length <= 1) {
          break;
        }
        // Removing a full grid step must not stop short — an off-grid count
        // would make the product revert to its minimum. Clamp to the shrinking
        // array (the clicked spread can sit within one increment of the end)
        // and walk back past non-removable spreads (the page math pins the
        // last inside spread of a spread book).
        let removeIndex = Math.min(spreadIndex, currentData.spreads.length - 1);
        while (removeIndex >= 0 && !currentData.spreads[removeIndex]?.canRemove) {
          removeIndex -= 1;
        }
        if (removeIndex < 0) {
          break;
        }
        dispatch(removeSpreadAction(removeIndex));
      }

      let state = getState();
      if (state.design.designData?.page_count) {
        dispatch(setPageCountAction(state.design.designData.page_count));
        dispatch(changeEcommerceVariantPageCountOperation(state.design.designData.page_count));
        sendPostMessage('design.switchToProduct', {
          productUid: state.design.designData.product_uid,
          pageCount: state.design.designData.page_count,
        });

        state = getState();
        const { product, configuration, selectedMultiOptions, selectedSingleOptions, replaceControlKeys } =
          state.variants;
        if (!configuration.singleSelection) {
          const matchingVariations = getAllMatchingVariations(
            product.productVariations,
            selectedMultiOptions,
            selectedSingleOptions,
            false,
            replaceControlKeys,
          );
          dispatch(checkForMissingTemplatesOperation(matchingVariations));
        }
      }
    });
  };

export default removeSpreadOperation;
